import {
  Badge,
  Button,
  DataTable,
  DefinitionList,
  ErrorState,
  FilterToolbar,
  Menu,
  Modal,
  MoreIcon,
  notify,
  PageHeader,
  SearchInput,
  Section,
  Select,
  SkeletonText,
  StatusBadge,
  TextField,
  Truncate,
  useClientRows,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type MenuEntry,
  type SortValue,
} from "@flexfreight/ui";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { useCursorPages } from "./cursorPages";

type Member = { id: string; name: string | null; email: string; role: string | null; legacyRole: string | null; mfa: boolean; mfaRequired: boolean; disabled: boolean; invitePending: boolean; lastSeen: string | null; welcomedAt?: string | null; createdAt: string };
const ROLE_LABEL: Record<string, string> = { owner: "Owner", admin: "Admin", ops: "Operations", sales: "Sales", marketing: "Marketing", read_only: "Read only" };
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const TEAM_SORT: Record<string, SortValue<Member>> = { person: (m) => m.name ?? m.email, role: (m) => ROLE_LABEL[m.role ?? ""] ?? null, seen: (m) => m.lastSeen };
const twoStep = (m: Member) => (m.mfa ? "on" : m.mfaRequired ? "required_off" : "off");

/** Admin Center > Team & permissions (A.38). */
export function TeamPage() {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const q = useQuery({ queryKey: ["console", "team"], queryFn: () => api<{ team: Member[]; roles: string[] }>("/v1/console/team") });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [sentTo, setSentTo] = useState<{ who: string; email: string; pending: boolean } | null>(null);
  const [linkFor, setLinkFor] = useState<{ who: string; link: string; expiresAt: string | null; connected: boolean } | null>(null);
  const welcome = useMutation({
    mutationFn: (m: Member) => api<{ emailConnected: boolean; emailSent: boolean; link: string | null; expiresAt: string | null }>(`/v1/console/team/${m.id}/welcome`, { method: "POST" }).then((r) => ({ r, m })),
    onSuccess: ({ r, m }) => {
      void qc.invalidateQueries({ queryKey: ["console", "team"] });
      if (r.emailSent) {
        notify.success(`Welcome emailed to ${m.email}`);
        setSentTo({ who: m.name ?? m.email, email: m.email, pending: m.invitePending });
      }
      else if (r.link) setLinkFor({ who: m.name ?? m.email, link: r.link, expiresAt: r.expiresAt, connected: r.emailConnected });
      else notify.error("Welcome not sent", r.emailConnected ? "The email didn't send. Try again." : "Email isn't connected. Connect Resend in Integrations.");
    },
    onError: (e) => notify.error("Welcome not sent", message(e)),
  });
  const remove = useMutation({
    mutationFn: (m: Member) => api(`/v1/console/team/${m.id}`, { method: "DELETE" }).then(() => m),
    onSuccess: (m) => {
      void qc.invalidateQueries({ queryKey: ["console", "team"] });
      notify.success(`${m.name ?? m.email} deleted`, "Their past actions stay in the audit log.");
    },
    onError: (e) => notify.error("Not deleted", message(e)),
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api(`/v1/console/team/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "team"] });
      notify.success("Saved", "They were signed out so the change applies right away.");
    },
    onError: (e) => notify.error("Not changed", message(e)),
  });
  const table = useClientRows(q.data?.team ?? [], { values: TEAM_SORT });
  const isOwner = me.role === "owner";
  const roleOptions = (q.data?.roles ?? []).filter((r) => r !== "owner" || isOwner).map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }));
  const columns: Column<Member>[] = [
    {
      key: "person",
      header: "Person",
      primary: true,
      sortable: true,
      truncate: "custom",
      render: (m) => (
        <span className="ffx-cellrow">
          <Truncate text={m.name ?? m.email} />
          {m.disabled ? <StatusBadge domain="staffAccount" value="deactivated" size="sm" /> : m.invitePending ? <StatusBadge domain="staffAccount" value="invite_pending" size="sm" /> : null}
        </span>
      ),
      sub: (m) => (
        <>
          {m.email}
          {welcome.isPending && welcome.variables?.id === m.id ? <span className="ffa-muted"> · Sending welcome...</span> : m.welcomedAt ? <span className="ffa-muted"> · Welcome emailed {when.format(new Date(m.welcomedAt))}</span> : null}
        </>
      ),
    },
    {
      key: "role",
      header: "Role",
      sortable: true,
      width: "12rem",
      render: (m) => {
        const self = m.id === me.user.id;
        const ownerLocked = m.role === "owner" && !isOwner;
        return self || ownerLocked || m.disabled ? (
          ROLE_LABEL[m.role ?? ""] ?? "None"
        ) : (
          <Select label={`Role for ${m.name ?? m.email}`} variant="toolbar" bareValue value={m.role ?? "read_only"} onChange={(v) => update.mutate({ id: m.id, patch: { role: v } })} options={roleOptions} />
        );
      },
      sub: (m) => (m.legacyRole ? `from legacy ${m.legacyRole}` : null),
    },
    { key: "twostep", header: "Two-step", width: "9rem", render: (m) => <StatusBadge domain="twoStep" value={twoStep(m)} size="sm" /> },
    { key: "seen", header: "Last sign-in", sortable: true, sortFirst: "desc", width: "10rem", render: (m) => (m.lastSeen ? <time dateTime={m.lastSeen}>{when.format(new Date(m.lastSeen))}</time> : <span className="ffa-muted">Never</span>) },
    {
      key: "actions",
      header: "Actions",
      align: "end",
      width: "5rem",
      hideable: false,
      render: (m) => {
        const self = m.id === me.user.id;
        const ownerLocked = m.role === "owner" && !isOwner;
        if (ownerLocked) return null;
        const who = m.name ?? m.email;
        const items: MenuEntry[] = self
          ? [{ label: "Edit name", onSelect: () => setEditing(m) }]
          : [
              { label: "Edit", onSelect: () => setEditing(m) },
              ...(m.disabled ? [] : [{ label: m.invitePending ? "Resend welcome email" : "Send welcome email", onSelect: () => welcome.mutate(m) }]),
              { type: "separator" as const },
              m.disabled
                ? { label: "Reactivate", onSelect: () => update.mutate({ id: m.id, patch: { disabled: false } }) }
                : {
                    label: "Deactivate",
                    onSelect: async () => {
                      if (await confirm({ title: `Deactivate ${who}?`, body: "They are signed out everywhere and can't sign in until reactivated.", confirmLabel: "Deactivate", cancelLabel: "Keep", danger: true })) update.mutate({ id: m.id, patch: { disabled: true } });
                    },
                  },
              {
                label: "Delete",
                danger: true,
                onSelect: async () => {
                  if (await confirm({ title: `Delete ${who}?`, body: "They are signed out everywhere and removed from the team. Their past actions stay in the audit log. This can't be undone.", confirmLabel: "Delete", cancelLabel: "Keep", danger: true })) remove.mutate(m);
                },
              },
            ];
        return <Menu look="icon" size="sm" trigger={<MoreIcon />} triggerLabel={`Actions for ${who}`} items={items} />;
      },
    },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        eyebrow="Admin Center"
        title="Team & permissions"
        subtitle="FlexFreight staff, their console role and two-step sign-in. Owners and admins must use two-step sign-in. Only an owner can grant or remove the owner role."
        primaryAction={<Button variant="primary" onClick={() => setInviting(true)}>Invite staff</Button>}
      />
      <DataTable
        caption="Staff"
        columns={columns}
        {...table}
        rowKey={(m) => m.id}
        loading={q.isPending}
        error={q.isError ? { title: "Couldn't load the team", message: message(q.error), onRetry: () => void q.refetch() } : null}
        empty="No staff accounts yet."
      />
      {inviting ? <InviteDialog roles={(q.data?.roles ?? []).filter((r) => r !== "owner" || isOwner)} onClose={() => setInviting(false)} /> : null}
      {editing ? <EditStaffDialog m={editing} self={editing.id === me.user.id} roleOptions={roleOptions} onClose={() => setEditing(null)} /> : null}
      {sentTo ? (
        <Modal
          open
          onClose={() => setSentTo(null)}
          title="Welcome email sent"
          description={`${sentTo.who} gets an email from FlexFreight at ${sentTo.email}. ${sentTo.pending ? "Its button activates their account (a new link; any older one no longer works)." : "Its button opens the admin portal sign-in."}`}
          footer={<Button variant="primary" onClick={() => setSentTo(null)}>Done</Button>}
        />
      ) : null}
      {linkFor ? (
        <Modal
          open
          onClose={() => setLinkFor(null)}
          title="Welcome link ready"
          description={`${linkFor.connected ? "The email didn't send" : "Email isn't connected"}, so copy this link and send it to ${linkFor.who} yourself.${linkFor.expiresAt ? ` It works once, until ${when.format(new Date(linkFor.expiresAt))}.` : ""}`}
          footer={<Button variant="primary" onClick={() => setLinkFor(null)}>Done</Button>}
        >
          <div className="ffa-inlineform">
            <TextField label="One-time link" value={linkFor.link} readOnly onFocus={(e) => e.currentTarget.select()} />
            <Button size="lg" onClick={() => void navigator.clipboard?.writeText(linkFor.link).then(() => notify.success("Link copied"))}>Copy link</Button>
          </div>
        </Modal>
      ) : null}
      {dialog}
    </section>
  );
}

/** Edit a staff member: name, role, and their email while the invite is still pending. */
function EditStaffDialog({ m, self, roleOptions, onClose }: { m: Member; self: boolean; roleOptions: { value: string; label: string }[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: m.name ?? "", email: m.email, role: m.role ?? "read_only" });
  const ownerLocked = m.role === "owner" && !roleOptions.some((o) => o.value === "owner");
  const patch: Record<string, string> = {};
  if (f.name.trim() && f.name.trim() !== (m.name ?? "")) patch.name = f.name.trim();
  if (!self && m.invitePending && f.email.trim().toLowerCase() !== m.email.toLowerCase()) patch.email = f.email.trim();
  if (!self && !ownerLocked && f.role !== (m.role ?? "read_only")) patch.role = f.role;
  const save = useMutation({
    mutationFn: () => api(`/v1/console/team/${m.id}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "team"] });
      notify.success("Saved", patch.email ? "Send a new welcome email to the new address." : patch.role ? "They were signed out so the new role applies right away." : undefined);
      onClose();
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const field = save.error instanceof ApiError ? (save.error.details as { field?: string } | undefined)?.field : undefined;
  const valid = f.name.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && Object.keys(patch).length > 0;
  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${m.name ?? m.email}`}
      description={m.invitePending ? "They haven't activated their account yet, so the email can still be corrected." : "The email can't change once they've activated their account."}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="ffx-fields">
        <TextField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} />
        <TextField label="Email" type="email" value={f.email} disabled={self || !m.invitePending} onChange={(e) => setF({ ...f, email: e.target.value })} error={field === "email" ? message(save.error) : undefined} />
        {self || ownerLocked ? null : <Select className="ffx-span" label="Role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={roleOptions} />}
      </div>
    </Modal>
  );
}

function InviteDialog({ roles, onClose }: { roles: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ email: "", name: "", role: "read_only" });
  // Emailed when email is connected (Resend chosen in Integrations); otherwise the admin-console link comes back once.
  const invite = useMutation({
    mutationFn: () => api<{ email: string; emailConnected: boolean; emailSent: boolean; link: string | null; expiresAt: string }>("/v1/console/team/invite", { method: "POST", body: { email: f.email.trim(), name: f.name.trim(), role: f.role } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "team"] });
      if (!r.link) {
        notify.success(`Invite emailed to ${r.email}`, `They choose a password on the admin console; the link works until ${when.format(new Date(r.expiresAt))}.`);
        onClose();
      }
    },
    onError: (e) => notify.error("Couldn't invite", message(e)),
  });
  const done = invite.data?.link ? invite.data : null;
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.name.trim() !== "";
  const why = done ? (done.emailConnected ? "The email didn't send, so copy this link and send it yourself." : "Email isn't connected, so copy this link and send it yourself.") : "";
  return (
    <Modal
      open
      onClose={onClose}
      title={done ? "Invite ready" : "Invite staff"}
      description={done ? `${why} It is shown once and works until ${when.format(new Date(done.expiresAt))}. ${f.name} sets a password on the admin console, then signs in here.` : "Use their FlexFreight email. Staff accounts are separate from customer accounts. With email connected the invite is emailed; otherwise you get a one-time link to pass on."}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!valid} loading={invite.isPending} onClick={() => invite.mutate()}>Create invite</Button>
          </>
        )
      }
    >
      {done ? (
        <div className="ffa-inlineform">
          <TextField label="One-time link" value={done.link ?? ""} readOnly onFocus={(e) => e.currentTarget.select()} />
          <Button size="lg" onClick={() => void navigator.clipboard?.writeText(done.link ?? "").then(() => notify.success("Link copied"))}>Copy link</Button>
        </div>
      ) : (
        <div className="ffx-fields">
          <TextField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} />
          <TextField label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Select className="ffx-span" label="Role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={roles.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))} />
        </div>
      )}
    </Modal>
  );
}

type AuditEvent = { id: string; at: string; action: string; actor: string; actorIp: string | null; targetType: string | null; targetId: string | null; details: unknown };
type AuditPage = { events: AuditEvent[]; families: string[]; next: string | null };
const AUDIT_FILTERS = { q: "", area: "" };

/** Admin Center > Audit log (A.41): every privileged action, newest first. Read-only. */
export function AuditPage() {
  const f = useUrlFilters(AUDIT_FILTERS, useSearchParams());
  const v = f.values;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }), 300);
  const list = useInfiniteQuery({
    queryKey: ["console", "audit", v.q, v.area],
    initialPageParam: "",
    queryFn: ({ pageParam }) => api<AuditPage>(`/v1/console/audit?${new URLSearchParams({ q: v.q, action: v.area, ...(pageParam ? { before: pageParam } : {}) })}`),
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
  });
  const pages = useCursorPages(list, (p) => p.events, `${v.q}|${v.area}`);
  const families = list.data?.pages[0]?.families ?? [];
  const columns: Column<AuditEvent>[] = [
    { key: "at", header: "When", width: "9.5rem", render: (e) => <time dateTime={e.at}>{when.format(new Date(e.at))}</time> },
    { key: "actor", header: "Who", width: "12rem", truncate: true, text: (e) => e.actor, render: (e) => e.actor, sub: (e) => e.actorIp },
    { key: "action", header: "Action", primary: true, render: (e) => <span className="ffx-num">{e.action}</span> },
    { key: "record", header: "Record", width: "12rem", mobile: "hide", render: (e) => (e.targetType ? `${e.targetType} ${String(e.targetId ?? "").slice(0, 12)}` : "-") },
    { key: "details", header: "Details", width: "24%", mobile: "hide", truncate: true, text: (e) => JSON.stringify(e.details), render: (e) => <span className="ffx-num ffa-small">{JSON.stringify(e.details)}</span> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader eyebrow="Admin Center" title="Audit log" subtitle="Every privileged action in the console: who, what, when and from where. Entries can't be edited or deleted." />
      <FilterToolbar
        label="Audit filters"
        search={<SearchInput label="Search people, actions or records" value={search} onChange={setSearch} />}
        items={[{ key: "area", priority: 1, active: v.area !== "", inline: <Select variant="toolbar" label="Area" value={v.area} active={v.area !== ""} onChange={(area) => f.set({ area })} options={[{ value: "", label: "All" }, ...families.map((x) => ({ value: x, label: x }))]} /> }]}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
      />
      <DataTable
        caption="Audit events"
        columns={columns}
        rows={pages.rows}
        rowKey={(e) => e.id}
        density="compact"
        loading={pages.loading || list.isFetching}
        error={list.isError ? { title: "Couldn't load the audit log", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive ? "No events match." : "No events yet."}
        pagination={pages.pagination}
      />
    </section>
  );
}

type Health = {
  at: string;
  uptimeSeconds: number;
  database: { ok: boolean; latencyMs: number };
  carrierMode: string;
  carriers: { carrier: string; mode: string; lastTestOk: boolean | null }[];
  integrations: { provider: string; name: string; status: string; lastTestAt: string | null; lastSyncAt: string | null }[];
  marketing: { outboundEnabled: boolean; autopilotEnabled: boolean };
  staff: { total: number; privilegedWithout2fa: number };
};
/** Older API builds sent "Key saved, …" sentences; the badge reads the state at the front. */
const integrationState = (s: string) => (s.startsWith("Key saved") ? "Key saved" : s);
const HEALTH_SECTIONS = ["Core", "Integrations", "Safety switches", "Staff security"];

/** Admin Center > System health (A.41). */
export function HealthPage() {
  const q = useQuery({ queryKey: ["console", "health"], queryFn: () => api<Health>("/v1/console/health"), refetchInterval: 30_000 });
  const h = q.data;
  const header = <PageHeader eyebrow="Admin Center" title="System health" subtitle="What the console is connected to right now. No keys or secrets are shown here." meta={h ? <span>Checked {when.format(new Date(h.at))} · refreshes every 30 seconds</span> : null} />;
  if (q.isError && !h) {
    return (
      <section className="ffa-page">
        {header}
        <ErrorState title="Couldn't load system health" message={message(q.error)} onRetry={() => void q.refetch()} />
      </section>
    );
  }
  return (
    <section className="ffa-page" aria-busy={!h || undefined}>
      {header}
      <div className="ffa-sectiongrid">
        {!h ? (
          HEALTH_SECTIONS.map((t) => (
            <Section key={t} title={t}>
              <SkeletonText lines={3} />
            </Section>
          ))
        ) : (
          <>
            <Section title="Core">
              <DefinitionList
                items={[
                  { term: "Database", detail: h.database.ok ? `OK (${h.database.latencyMs} ms)` : "Down" },
                  { term: "API uptime", detail: `${Math.floor(h.uptimeSeconds / 3600)} h ${Math.floor((h.uptimeSeconds % 3600) / 60)} min` },
                  { term: "Carrier mode (server)", detail: h.carrierMode === "mock" ? "Mock: every carrier is simulated" : h.carrierMode },
                ]}
              />
            </Section>
            <Section title="Integrations">
              <DefinitionList items={h.integrations.map((i) => ({ key: i.provider, term: i.name, detail: <StatusBadge domain="integration" value={integrationState(i.status)} size="sm" /> }))} />
            </Section>
            <Section title="Safety switches">
              <DefinitionList
                items={[
                  { term: "Marketing sending", detail: h.marketing.outboundEnabled ? "On" : "Off" },
                  { term: "Marketing autopilot", detail: h.marketing.autopilotEnabled ? "On" : "Off" },
                  { term: "Live carriers", detail: h.carriers.filter((c) => c.mode === "live").map((c) => c.carrier.toUpperCase()).join(", ") || "None" },
                ]}
              />
            </Section>
            <Section title="Staff security">
              <DefinitionList
                items={[
                  { term: "Staff accounts", detail: <span className="ffx-num">{h.staff.total}</span> },
                  { term: "Owners/admins without two-step", detail: h.staff.privilegedWithout2fa ? <Badge tone="danger" size="sm">{h.staff.privilegedWithout2fa}</Badge> : "None" },
                ]}
              />
            </Section>
          </>
        )}
      </div>
    </section>
  );
}
